import {
  EllipsisVertical,
  Hourglass,
  Mail,
  Paperclip,
  Plus,
  Sparkles,
  Star,
  type LucideIcon,
} from 'lucide-react';
import { Suspense, lazy, useState } from 'react';
import { Dropdown, DropdownContent, DropdownItem, DropdownTrigger, Toggle, cn } from '@waymail/ui';
import { useBuscasSalvas } from '../lib/consultas';
import { useUi } from '../store/ui';
import type { PedidoDeBusca } from './DialogoDeBuscaSalva';

const DialogoDeBuscaSalva = lazy(() =>
  import('./DialogoDeBuscaSalva').then((m) => ({ default: m.DialogoDeBuscaSalva })),
);

/** Presets que ja vem prontos (WYM-12): so a consulta muda, na linguagem de `analisarConsulta`. */
export const PRESETS: { chave: string; nome: string; query: string; Icone: LucideIcon }[] = [
  { chave: 'preset:sem-resposta', nome: 'Sem resposta', query: 'is:unanswered', Icone: Hourglass },
  { chave: 'preset:anexos', nome: 'Com anexos', query: 'has:attachment', Icone: Paperclip },
  { chave: 'preset:nao-lidas', nome: 'Não lidas', query: 'is:unread', Icone: Mail },
  { chave: 'preset:importantes', nome: 'Importantes', query: 'is:flagged', Icone: Star },
];

/**
 * "Pastas inteligentes" (WYM-12, ADR 0060): visualizacoes virtuais por
 * condicao. Nao movem nem copiam mensagem, e por isso nunca ganham as acoes de
 * pasta IMAP (mover, esvaziar): so renomear e excluir a visualizacao.
 */
export function PastasInteligentes({ accountId }: { accountId: string | null }) {
  const buscas = useBuscasSalvas();
  const aberta = useUi((s) => s.pastaInteligente);
  const abrir = useUi((s) => s.abrirPastaInteligente);
  const [todasAsContas, setTodasAsContas] = useState(
    aberta?.chave.startsWith('preset:') === true && aberta.accountId === null,
  );
  const [pedido, setPedido] = useState<PedidoDeBusca | null>(null);

  const escopoDosPresets = todasAsContas ? null : accountId;
  const personalizadas = (buscas.data ?? []).filter(
    (b) => b.smart && (b.accountId === null || b.accountId === accountId),
  );

  return (
    <section aria-label="Pastas inteligentes" className="mt-5">
      <div className="flex items-center justify-between pr-1">
        <p className="flex items-center gap-1.5 px-2.5 text-[12px] font-semibold text-secondary">
          <Sparkles size={14} strokeWidth={1.5} aria-hidden="true" />
          Pastas inteligentes
        </p>
        <button
          type="button"
          aria-label="Nova pasta inteligente"
          title="Nova pasta inteligente"
          onClick={() => setPedido({ tipo: 'salvar', query: '', accountId, inteligente: true })}
          className="flex size-7 toque:size-10 cursor-pointer items-center justify-center rounded-md text-tertiary hover:bg-hover hover:text-primary"
        >
          <Plus size={16} strokeWidth={1.5} aria-hidden="true" />
        </button>
      </div>
      <p className="px-2.5 pb-1 text-[11px] text-secondary">Visualizações: não movem mensagens.</p>

      <div className="px-2.5 py-1">
        <Toggle
          label="Todas as contas"
          checked={todasAsContas}
          onChange={(e) => {
            const todas = e.target.checked;
            setTodasAsContas(todas);
            // A que esta aberta acompanha o escopo, sem precisar clicar de novo.
            const p = PRESETS.find((x) => x.chave === aberta?.chave);
            if (p !== undefined) {
              abrir({
                chave: p.chave,
                nome: p.nome,
                query: p.query,
                accountId: todas ? null : accountId,
              });
            }
          }}
        />
      </div>

      <ul className="mt-1 flex flex-col gap-0.5">
        {PRESETS.map((p) => (
          <Linha
            key={p.chave}
            Icone={p.Icone}
            nome={p.nome}
            titulo={p.query}
            ativa={aberta?.chave === p.chave}
            onAbrir={() =>
              abrir({ chave: p.chave, nome: p.nome, query: p.query, accountId: escopoDosPresets })
            }
          />
        ))}
        {personalizadas.map((b) => (
          <Linha
            key={b.id}
            Icone={Sparkles}
            nome={b.name}
            titulo={b.query}
            ativa={aberta?.chave === b.id}
            onAbrir={() =>
              abrir({ chave: b.id, nome: b.name, query: b.query, accountId: b.accountId })
            }
            acoes={
              <Dropdown>
                <DropdownTrigger asChild>
                  <button
                    type="button"
                    aria-label={`Ações da pasta inteligente ${b.name}`}
                    className="flex size-7 toque:size-10 shrink-0 cursor-pointer items-center justify-center rounded-md text-tertiary hover:bg-hover hover:text-primary"
                  >
                    <EllipsisVertical size={16} strokeWidth={1.5} aria-hidden="true" />
                  </button>
                </DropdownTrigger>
                <DropdownContent align="end">
                  <DropdownItem onSelect={() => setPedido({ tipo: 'renomear', busca: b })}>
                    Renomear
                  </DropdownItem>
                  <DropdownItem danger onSelect={() => setPedido({ tipo: 'excluir', busca: b })}>
                    Excluir
                  </DropdownItem>
                </DropdownContent>
              </Dropdown>
            }
          />
        ))}
      </ul>

      {pedido !== null && (
        <Suspense fallback={null}>
          <DialogoDeBuscaSalva pedido={pedido} onFechar={() => setPedido(null)} />
        </Suspense>
      )}
    </section>
  );
}

function Linha({
  Icone,
  nome,
  titulo,
  ativa,
  onAbrir,
  acoes,
}: {
  Icone: LucideIcon;
  nome: string;
  titulo: string;
  ativa: boolean;
  onAbrir: () => void;
  acoes?: React.ReactNode;
}) {
  return (
    <li className="relative flex items-center">
      <button
        type="button"
        onClick={onAbrir}
        title={titulo}
        aria-current={ativa ? 'page' : undefined}
        className={cn(
          'flex h-9 w-full min-w-0 cursor-pointer items-center gap-2.5 rounded-lg px-2.5 text-left text-[13px] font-semibold',
          acoes !== undefined && 'pr-9',
          ativa ? 'bg-selected text-accent' : 'text-primary hover:bg-hover',
        )}
      >
        <Icone
          size={16}
          strokeWidth={1.5}
          aria-hidden="true"
          className={cn('shrink-0', ativa ? 'text-accent' : 'text-tertiary')}
        />
        <span className="truncate">{nome}</span>
      </button>
      {acoes !== undefined && <span className="absolute right-1">{acoes}</span>}
    </li>
  );
}
